/**
 * MODULE: _shared/frontend/components/Modal.tsx
 * PURPOSE: Accessible modal dialog with focus trap; close button always visible in top-right (with or without title).
 * DEPENDENCIES: react, react-dom (createPortal), focus-trap-react, ../hooks/useOnClickOutside, ../utils/cn.
 * PUBLIC_API: Modal (default), ModalProps (exported interface).
 * SIDE_EFFECTS: Portals to document.body; body overflow lock when open; focus trap; Escape key listener; click-outside handler.
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: 134 / 200 lines
 * Recent: 2026-02-07 - Close button always rendered (fix for modals without title). See CHANGELOG.md [2.2.23].
 */

import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import FocusTrap from 'focus-trap-react';
import { useOnClickOutside } from '../hooks/useOnClickOutside';
import { cn } from '../utils/cn';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
}

/**
 * Modal - Accessible modal dialog component with focus management
 * 
 * Required CSS classes:
 * - .modal-overlay - Overlay backdrop
 * - .modal-content - Modal container
 * - .modal-sm, .modal-md, .modal-lg, .modal-xl - Size variants
 * - .modal-header - Header section
 * - .modal-title - Title text
 * - .modal-close - Close button
 * - .modal-body - Content area
 * 
 * Accessibility:
 * - Keyboard: Escape (close), Tab (focus trap)
 * - Screen reader: Announces dialog role and title
 * - Focus: Traps focus within modal, restores on close
 * - Body scroll: Locks background scrolling when open
 * 
 * @example
 * <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Confirm">
 *   <p>Are you sure?</p>
 * </Modal>
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useOnClickOutside(modalRef, () => {
    if (closeOnOverlayClick) onClose();
  });

  // Focus management and body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    // Store the element that had focus before opening
    returnFocusRef.current = document.activeElement as HTMLElement;

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    // Focus trap: Find all focusable elements within modal
    const focusableElements = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as NodeListOf<HTMLElement> | undefined;

    if (focusableElements && focusableElements.length > 0) {
      // Focus first focusable element
      focusableElements[0]?.focus();
    }

    return () => {
      // Restore body scroll
      document.body.style.overflow = '';
      // Return focus to previous element
      setTimeout(() => {
        returnFocusRef.current?.focus();
      }, 0);
    };
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  return createPortal(
    <FocusTrap>
      <div 
        className="modal-overlay" 
        role="dialog" 
        aria-modal="true" 
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        <div ref={modalRef} className={cn('modal-content', `modal-${size}`)}>
          {/* Always render close button in top right corner */}
          <button
            onClick={onClose}
            className="modal-close"
            aria-label="Close modal"
            type="button"
          >
            ×
          </button>

          {title && (
            <div className="modal-header">
              <h2 id="modal-title" className="modal-title">{title}</h2>
            </div>
          )}
          <div className="modal-body">{children}</div>
        </div>
      </div>
    </FocusTrap>,
    document.body
  );
}
